import { useEffect, useState } from "react";
import Div from "../../../../../components/base/Div";
import Indicator from "../../../../../components/base/Indicator";
import { useHub } from "../../../../../core/Global";
import FlexRow from "../../../../../components/base/FlexRow";
import TextBox from "../../../../../components/form/TextBox";
import Form from "../../../../../components/form/Form";
import ToggleSwitch from "../../../../../components/form/ToggleSwitch";
import { toastError, toastSuccess } from "../../../../../components/base/Toast";



interface IPayKeys {
    publicKey: string
    privateKey: string
    tapToPayEnabled: boolean
    /// The keys belong to a real gateway account rather than a sandbox one.
    liveMode: boolean
    /// Whether the person looking at this page may touch the switch: our own staff, or the
    /// account holder themselves. Nobody else on the account — getting it wrong stops Tap to Pay
    /// setting up on every phone in the company.
    canEditLiveMode: boolean
}

export default function PrimeGatewayToken() {

    const hub = useHub()
    const [fetching, fetchingSet] = useState<boolean>(true)
    const [fetched, fetchedSet] = useState<boolean>(false)

    const [model, modelSet] = useState<IPayKeys | null>(null)


    const [working, workingSet] = useState<boolean>(false)
    const [togglingTapToPay, togglingTapToPaySet] = useState<boolean>(false)
    const [togglingLiveMode, togglingLiveModeSet] = useState<boolean>(false)

    const saveLiveMode = (live: boolean) => {
        togglingLiveModeSet(true)
        hub.Post("/accounts/profile/paymentkeys/livemode/", { liveMode: live }, res => {
            if (res.success) {
                modelSet(m => m ? { ...m, liveMode: live } : m)
                toastSuccess(live ? "Gateway set to LIVE" : "Gateway set to TEST")
            }
            else {
                toastError(res.message ?? "An error occurred")
            }
            togglingLiveModeSet(false)
        })
    }

    const saveTapToPay = (enabled: boolean) => {
        togglingTapToPaySet(true)
        hub.Post("/accounts/profile/paymentkeys/taptopay/", { enabled }, res => {
            if (res.success) {
                modelSet(m => m ? { ...m, tapToPayEnabled: enabled } : m)
                toastSuccess(enabled ? "Tap to Pay enabled" : "Tap to Pay disabled")
            }
            else {
                toastError(res.message ?? "An error occurred")
            }
            togglingTapToPaySet(false)
        })
    }

    const fetch = () => {
        hub.Get("/accounts/profile/paymentkeys/", res => {
            modelSet(res.data)
            fetchingSet(false)
            fetchedSet(true)
        })
    }
    useEffect(fetch, [])

    return (
        <div className="card mb-5">
            <div className="card-header border-0">
                <div className="card-title m-0">
                    <h3 className="fw-bolder m-0">StellarPay Integration</h3>
                </div>
                <div className="card-action">
                    <a href="https://portal.stellarpay.com/form/merchant-signup-rinvox"
                        target="_blank" className="btn btn-sm btn-light-primary">
                        Register & Get Keys
                    </a>
                </div>
            </div>
            <div className="card-body border-top p-9">

                <Div visible={fetching} className="my-20">
                    <Indicator />
                </Div>

                <Div visible={fetched}>
                    <p>
                        To enable payments, you need to create an account on StellarPay and generate your API keys. <br />
                        <a href="https://portal.stellarpay.com/form/merchant-signup-rinvox" target="_blank">Click here to sign up!</a> Then return to this page and paste your keys below.
                    </p>
                    <Form onSubmit={body => {
                        workingSet(true)
                        hub.Post("/accounts/profile/paymentkeys/", body, res => {
                            if (res.success && res.data) {
                                modelSet(res.data)
                            }
                            else if (res.field) {
                                window.formError(res.field, res.message ?? "An error occurred");
                            }
                            else {
                                toastError(res.message ?? "An error occurred")
                            }
                            workingSet(false)
                        })
                    }}>
                        <div style={{
                            maxWidth: 800
                        }}>

                            <TextBox id="txt-prime-private-key" name="privateKey"
                                label="Private Key" block labelUp value={model?.privateKey} />

                            <TextBox id="txt-prime-public-key" name="publicKey"
                                label="Public Key" block labelUp value={model?.publicKey} />

                            <FlexRow>
                                <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                                    {
                                        working ? <Indicator text="Please wait..." /> : <>Verify & Save the Keys</>
                                    }
                                </button>
                            </FlexRow>

                        </div>

                    </Form>

                    {/* The account holder's and ours; not the rest of their staff's. */}
                    <Div visible={model?.canEditLiveMode === true}>
                        <div className="separator my-8"></div>

                        <div className="d-flex flex-stack flex-wrap gap-3" style={{ maxWidth: 800 }}>
                            <div className="me-4">
                                <h4 className="fw-bolder text-gray-900 mb-1">
                                    Live Mode
                                    <span className={`badge ms-3 ${model?.liveMode ? "badge-light-success" : "badge-light-warning"}`}>
                                        {model?.liveMode ? "LIVE" : "TEST"}
                                    </span>
                                </h4>
                                <div className="fs-6 text-gray-600">
                                    Whether the keys above belong to a real gateway account or a sandbox one.
                                    The card reader is configured against this — a live key will not work
                                    against the test environment, or the other way round.
                                </div>
                            </div>
                            <div className="d-flex align-items-center flex-shrink-0">
                                <ToggleSwitch raw name="liveMode"
                                    checked={model?.liveMode === true}
                                    onChange={saveLiveMode} />
                                {togglingLiveMode && <span className="ms-3"><Indicator /></span>}
                            </div>
                        </div>
                    </Div>

                    {/* Tap to Pay is only relevant once the gateway keys are saved. */}
                    <Div visible={!!model?.privateKey}>
                        <div className="separator my-8"></div>

                        <div className="d-flex flex-stack flex-wrap gap-3" style={{ maxWidth: 800 }}>
                            <div className="me-4">
                                <h4 className="fw-bolder text-gray-900 mb-1">Tap to Pay on iPhone</h4>
                                <div className="fs-6 text-gray-600">
                                    Let technicians accept contactless cards and Apple Pay right on their iPhone.
                                    When off, Tap to Pay is hidden everywhere in the app.
                                </div>
                            </div>
                            <div className="d-flex align-items-center flex-shrink-0">
                                <ToggleSwitch raw name="tapToPayEnabled"
                                    checked={model?.tapToPayEnabled === true}
                                    onChange={saveTapToPay} />
                                {togglingTapToPay && <span className="ms-3"><Indicator /></span>}
                            </div>
                        </div>
                    </Div>
                </Div>

            </div>
        </div>
    )
}